// Billing history — a member's past IFT plan payments with printable receipts.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Loader2, ArrowRight, FileText } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const fmtInr = (paise) => `₹${((paise || 0) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function BillingHistory() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!user) return;
    http.get("/pricing/my-payments").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
    http.get("/pricing/my-subscription").then((r) => setSub(r.data)).catch(() => {});
  }, [user]);

  const openReceipt = async (orderId) => {
    try {
      const { data } = await http.get(`/pricing/receipt/${orderId}`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (w) { w.document.write(data); w.document.close(); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open the receipt.");
    }
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="billing-signin-required">
        <Receipt className="w-8 h-8 mx-auto opacity-40" />
        <h1 className="font-display text-3xl mt-4">Billing & receipts</h1>
        <p className="mt-2 text-sm opacity-70">Please sign in to view your payment history.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-12" data-testid="billing-history-page">
      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>YOUR ACCOUNT</div>
      <h1 className="font-display text-4xl">Billing & receipts</h1>
      {sub && (
        <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }} data-testid="billing-current-plan">
          Current plan: <strong>{sub.plan_name}</strong>
          {sub.node_limit ? ` · up to ${sub.node_limit} family members` : " · unlimited members"}
          {sub.subscription?.renews_on ? ` · valid until ${sub.subscription.renews_on}` : ""}
          {" — "}
          <Link to="/pricing" className="underline">view plans</Link>
        </p>
      )}

      <div className="mt-8">
        {items === null ? (
          <div className="wofy-card text-center py-10"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : items.length === 0 ? (
          <div className="wofy-card text-center py-10" data-testid="billing-empty">
            <FileText className="w-6 h-6 mx-auto opacity-40 mb-2" />
            <p className="text-sm opacity-70">No payments yet. Your receipts will appear here.</p>
            <Link to="/pricing" className="wofy-btn-primary inline-flex items-center gap-2 mt-4">
              Explore plans <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="wofy-card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] font-mono uppercase tracking-wider opacity-60 border-b" style={{ borderColor: "var(--wofy-border)" }}>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.session_id} className="border-b last:border-0" style={{ borderColor: "var(--wofy-border)" }} data-testid={`billing-row-${p.session_id}`}>
                    <td className="px-4 py-3 whitespace-nowrap">{(p.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">{p.plan_name} <span className="opacity-60 text-xs">({p.billing_period || "yearly"})</span></td>
                    <td className="px-4 py-3 font-semibold whitespace-nowrap">{fmtInr(p.amount)}</td>
                    <td className="px-4 py-3">
                      <span
                        className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
                        style={p.payment_status === "paid"
                          ? { background: "#DCFCE7", color: "#166534" }
                          : { background: "#FEF3C7", color: "#92400E" }}
                      >
                        {p.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.payment_status === "paid" ? (
                        <button
                          onClick={() => openReceipt(p.session_id)}
                          className="inline-flex items-center gap-1 text-xs underline hover:opacity-70"
                          data-testid={`billing-receipt-${p.session_id}`}
                        >
                          <Receipt className="w-3.5 h-3.5" /> View / Download
                        </button>
                      ) : (
                        <span className="text-xs opacity-40">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="mt-4 text-xs opacity-60">Receipts open in a new tab — use "Print / Save as PDF" to download a copy.</p>
    </div>
  );
}
